// Kontakt auf einen Blick (Party 360, erster Schnitt; ST-ACT-001, Eintrag vom
// 28.09.2026; CM-ACT-001, NFR-UX-004).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// context-roles.ts und interactions.ts. Es leitet den Kopf und die
// Aktionsleiste des Kontaktdetails AUSSCHLIESSLICH aus Antworten ab, die der
// Bildschirm ohnehin laedt: dem Kontakt (fetchParty) und der ersten Seite des
// Verlaufs (fetchInteractions). Kein neuer Aufruf, keine neue Route.
//
// WAS DER KOPF BEWUSST NICHT ZEIGT: die Rolle im eigenen Haus. Sie kommt aus
// einem eigenen Abruf des Abschnitts "Zugehoerigkeit"; im Kopf stuende sie als
// zweite Kopie desselben Zustands und nach jedem Aendern oder Beenden veraltet
// da (Challenge-Review 28.09.2026, P1-3). Reviewpunkt: ein gemeinsamer Zustand
// fuer Kopf und Abschnitt.

import { addressesHref } from './addresses.ts';
import { contactPointsHref, identifiersHref, organizationHref, type ContactPointView, type PartyDetail } from './parties.ts';
import { notWritableNotice } from './party-lifecycle.ts';

export interface PreferredContact {
  /** "E-Mail" oder "Telefon" -- in Worten, nicht als Kanalcode. */
  readonly channelLabel: string;
  readonly value: string;
}

/**
 * Der bevorzugte Kontaktweg fuer den Kopf: zuerst die bevorzugte
 * Telefonnummer, dann die bevorzugte E-Mail-Adresse -- "wer anruft, sucht die
 * Nummer" (kontakt.tsx, Dateikopf). Ist keiner als bevorzugt gekennzeichnet,
 * zeigt der Kopf keinen: einen Weg zu erfinden, den niemand bevorzugt hat,
 * waere eine Aussage, die niemand getroffen hat.
 */
export function preferredContact(party: Pick<PartyDetail, 'emailAddresses' | 'phoneNumbers'>): PreferredContact | undefined {
  const phone = party.phoneNumbers.find((entry: ContactPointView) => entry.isPreferred);
  if (phone !== undefined) {
    return { channelLabel: 'Telefon', value: phone.value };
  }
  const email = party.emailAddresses.find((entry: ContactPointView) => entry.isPreferred);
  return email === undefined ? undefined : { channelLabel: 'E-Mail', value: email.value };
}

/**
 * Der Stand, den der Verlauf nach jedem Laden meldet: die Summe der offenen
 * Wiedervorlagen ueber die GELADENEN Notizen und ob es weitere gibt.
 */
export interface FollowUpTally {
  readonly openFollowUps: number;
  /** Die Zahl der geladenen Eintraege; das Register nennt sie ("Verlauf (5)"). */
  readonly entryCount: number;
  readonly hasMore: boolean;
}

/**
 * Der Satz im Kopf.
 *
 * WAS DIE ZAHL IST, und der Satz sagt genau das (Challenge-Review P1-2,
 * Dirigent 28.09.2026): `openFollowUpCount` zaehlt je Notiz die offenen
 * Wiedervorlagen ALLER Personen im eigenen Haus (interaction-reader.ts). Die
 * Summe ist also "offen zu diesem Kontakt in deinem Haus" -- nicht "deine"
 * und nicht die Zahl des ganzen Hauses. Laedt der Verlauf nicht alle Notizen
 * (hasMore), ist sie eine Untergrenze und heisst "mindestens".
 *
 * Bei null steht kein Satz da: eine Zeile "keine offenen Wiedervorlagen" auf
 * jedem Kontakt ohne Vorgang waere Laerm im Kopf (5.2 Nr. 14). Mit weiteren
 * Seiten ist "keine" nicht belegt -- dann ebenfalls kein Satz.
 */
export function followUpSummary(tally: FollowUpTally | undefined): string | undefined {
  if (tally === undefined || tally.openFollowUps <= 0) {
    return undefined;
  }
  const menge = tally.hasMore ? `mindestens ${tally.openFollowUps}` : String(tally.openFollowUps);
  const wort = tally.openFollowUps === 1 ? 'offene Wiedervorlage' : 'offene Wiedervorlagen';
  return `${menge} ${wort} zu diesem Kontakt in deinem Verlag`;
}

/** Die Kurzform fuer den Kontaktkopf (v4): nur die Zahl; den Bezug liest der Kopf aus followUpSummary vor. */
export function followUpShort(tally: FollowUpTally | undefined): string | undefined {
  if (tally === undefined || tally.openFollowUps <= 0) {
    return undefined;
  }
  const menge = tally.hasMore ? `mindestens ${tally.openFollowUps}` : String(tally.openFollowUps);
  return `${menge} ${tally.openFollowUps === 1 ? 'offene Wiedervorlage' : 'offene Wiedervorlagen'}`;
}

/** Die Summe ueber eine geladene Verlaufsseite. */
export function tallyFollowUps(entries: readonly { readonly openFollowUpCount: number }[], hasMore: boolean): FollowUpTally {
  return {
    openFollowUps: entries.reduce((summe, entry) => summe + Math.max(0, entry.openFollowUpCount), 0),
    entryCount: entries.length,
    hasMore,
  };
}

export interface CareLink {
  readonly label: string;
  readonly href: string;
}

/**
 * Die Pflegewege der Aktionsleiste -- dieselben Ziele wie bisher in der
 * Fusszeile, jetzt sichtbar unter dem Kopf. Organisationsnamen nur fuer
 * Organisationen: ein Weg zu einem Bildschirm, den es fuer Personen nicht
 * gibt, waere die Attrappe aus ADR-0002.
 */
export function careLinks(party: Pick<PartyDetail, 'partyId' | 'organization'> & { readonly lifecycleStatus?: string }): readonly CareLink[] {
  // An einem nicht beschreibbaren Kontakt zeigen die Seiten nur noch an: das Wort "pflegen" waere falsch.
  const pflegen = notWritableNotice(party.lifecycleStatus) === undefined ? ' pflegen' : '';
  const links: CareLink[] = [
    { label: `Kontaktwege${pflegen}`, href: contactPointsHref(party.partyId) },
    { label: `Anschriften${pflegen}`, href: addressesHref(party.partyId) },
    { label: `Kennungen${pflegen}`, href: identifiersHref(party.partyId) },
  ];
  if (party.organization !== undefined) {
    links.push({ label: `Organisationsnamen${pflegen}`, href: organizationHref(party.partyId) });
  }
  return links;
}

/**
 * Die Bezeichnung des Registers "Verlauf": die Zahl der geladenen Eintraege,
 * mit Plus, wenn es weitere gibt. Vor dem Laden (kein Stand) heisst es nur
 * "Verlauf". Der Name beginnt immer mit "Verlauf".
 */
export function verlaufBezeichnung(tally: FollowUpTally | undefined): string {
  if (tally === undefined) {
    return 'Verlauf';
  }
  return `Verlauf (${tally.entryCount}${tally.hasMore ? '+' : ''})`;
}
